import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Formik } from "formik";
import * as yup from "yup";
import Form from "react-bootstrap/Form";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";

import { ImageUpload } from "../../../fragments/ImageUpload";
import { userAdminHandler, masterHandler } from "../../../redux/handlers";
import { UserAdminSuccessModal } from "./modals/UserAdminSuccessModal";

import {
  ChangeEmailModal,
  ResetpasswordModal,
  ChangeEmailSuccessModal,
  ResetpasswordSuccessModal,
} from "./modals";

const INITIAL_FORM_STATE = {
  prefix: "",
  name: "",
  surname: "",
  gender: "",
  age: "",
  nickname: "",
  code: "",
  birth_date: null,
  image_path: "",
  phone_number: "",

  empEmail: "",
  password: "",
};

const MODAL_CONFIG = {
  BASE: {
    open: false,
    x: 0,
    y: 0,
    width: 460,
    pin: false,
    mode: "normal",
    modalname: "create",
  },
  HEIGHTS: {
    EMAIL: 360,
    RESET_PASSWORD: 400,
    SUCCESS: 350,
  },
};

const MODAL_STATES = {
  EMAIL: { ...MODAL_CONFIG.BASE, height: MODAL_CONFIG.HEIGHTS.EMAIL },
  RESET_PASSWORD: {
    ...MODAL_CONFIG.BASE,
    height: MODAL_CONFIG.HEIGHTS.RESET_PASSWORD,
  },
  SUCCESS: { ...MODAL_CONFIG.BASE, height: MODAL_CONFIG.HEIGHTS.SUCCESS },
};

const VALIDATION_SCHEMA = yup.object().shape({
  prefix: yup.string().required("กรุณาเลือกคำนำหน้า"),
  name: yup.string().required("กรุณากรอกชื่อ"),
  surname: yup.string().required("กรุณากรอกนามสกุล"),
  nickname: yup.string().required("กรุณากรอกชื่อเล่น"),
  gender: yup.string().required("กรุณาเลือกเพศ"),

  empEmail: yup.string().email("อีเมลไม่ถูกต้อง").required("กรุณากรอกอีเมล"),
  password: yup.string().when("$isCreate", {
    is: true,
    then: () =>
      yup
        .string()
        .min(8, "รหัสผ่านต้องมีความยาวอย่างน้อย 8 ตัวอักษร")
        .required("กรุณากรอกรหัสผ่าน"),
    otherwise: () => yup.string(),
  }),
});

export function UserAdminForm() {
  const { adminID } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const prefixNames = useSelector((state) => state.master.prefixNames) || [];
  const genders = useSelector((state) => state.master.genders) || [];

  const [isFormDisabled, setIsFormDisabled] = useState(adminID);
  const [formData, setFormData] = useState(INITIAL_FORM_STATE);
  const [emailModal, setEmailModal] = useState(MODAL_STATES.EMAIL);
  const [resetPasswordModal, setResetPasswordModal] = useState(
    MODAL_STATES.RESET_PASSWORD
  );
  const [resetPasswordSuccessModal, setResetPasswordSuccessModal] = useState(
    MODAL_STATES.SUCCESS
  );
  const [emailSuccessModal, setEmailSuccessModal] = useState(
    MODAL_STATES.SUCCESS
  );

  const [modalSuccess, setModalSuccess] = useState({
    open: false,
    x: 0,
    y: 0,
    width: 460,
    height: 350,
    pin: false,
    mode: "normal",
    modalname: "create",
  });

  const calculateAge = (birthDate) => {
    if (!birthDate) return "";
    return dayjs().diff(dayjs(birthDate), "year");
  };

  const fetchUserData = useCallback(async () => {
    try {
      if (!adminID) return;

      const response = await dispatch(
        userAdminHandler.getUserAdminById(adminID)
      );

      if (response) {
        const userData = response;
        setFormData({
          prefix: userData.prefixName?.name || "",
          name: userData.first_name || "",
          surname: userData.last_name || "",
          gender: userData.gender?.id.toString() || "",
          nickname: userData.nickname || "",
          code: userData.code || "",
          birth_date: userData.birth_date ? dayjs(userData.birth_date) : null,
          age: calculateAge(userData.birth_date) || "",
          image_path: userData.image_path || "",
          phone_number: userData.phone_number || "",
          empEmail: userData.email || "",
          password: "********",
        });
      }
    } catch (error) {
      console.error("Error fetching user data:", error);
    }
  }, [adminID, dispatch]);

  useEffect(() => {
    const initializeData = async () => {
      await dispatch(masterHandler.getPrefixNames("NATURAL"));
      await dispatch(masterHandler.getGenders());
      if (adminID) {
        await fetchUserData();
      }
    };

    initializeData();
  }, [dispatch, fetchUserData, adminID]);

  const handleFormSubmit = async (values) => {
    try {
      const prefixId = prefixNames.find((p) => p.name === values.prefix)?.id;
      if (!prefixId) throw new Error("กรุณาเลือกคำนำหน้า");

      const submitData = {
        prefix_name_id: Number(prefixId),
        gender_id: Number(values.gender),
        first_name: values.name.trim(),
        last_name: values.surname.trim(),
        nickname: values.nickname.trim(),
        phone_number: values.phone_number?.trim(),
        birth_date: values.birth_date ? values.birth_date.toISOString() : null,
        email: values.empEmail,
        password: values.password !== "********" ? values.password : undefined,
      };

      if (
        values.image_path &&
        (values.image_path.startsWith("data:image") || values.image_path)
      ) {
        submitData.image_path = values.image_path;
      }
      let result;
      if (adminID) {
        result = await dispatch(
          userAdminHandler.updateUserAdmin(adminID, submitData)
        );
      } else {
        result = await dispatch(userAdminHandler.createUserAdmin(submitData));
      }

      if (result) {
        setModalSuccess({
          ...modalSuccess,
          open: true,
        });
        setTimeout(() => {
          setModalSuccess({
            ...modalSuccess,
            open: false,
          });
          navigate("/setting/users");
        }, 1000);
      }
    } catch (error) {
      console.error("Error updating/creating admin:", error);
    }
  };

  return (
    <>
      <div className="flex flex-col min-h-screen">
        <div className="flex-grow">
          <Formik
            enableReinitialize
            initialValues={formData}
            validationSchema={VALIDATION_SCHEMA}
            onSubmit={handleFormSubmit}
            validationContext={{ isCreate: !adminID }}
          >
            {({
              handleSubmit,
              handleChange,
              touched,
              isValid,
              errors,
              values,
              setFieldValue,
            }) => (
              <Form noValidate onSubmit={handleSubmit}>
                <Card className="!rounded-lg mb-4">
                  <div className="p-4">
                    <div>
                      <div className="text-[#4C4E64] font-semibold text-xl mb-4">
                        ข้อมูลทั่วไป
                      </div>

                      <ImageUpload
                        setFieldValue={setFieldValue}
                        values={{ image_path: values.image_path }}
                        onUploadSuccess={(fileInfo) => {
                          setFieldValue("image_path", fileInfo.base64);
                        }}
                        onUploadError={(error) => {}}
                      />

                      <div className="grid grid-cols-12 gap-4">
                        <div className="md:col-span-3 col-span-6">
                          <TextField
                            label="รหัส ADMIN"
                            variant="outlined"
                            InputProps={{ sx: { borderRadius: "7px" } }}
                            fullWidth
                            disabled={true}
                            sx={{
                              ".Mui-disabled": {
                                background: "#F8F8F9",
                              },
                            }}
                            onChange={handleChange}
                            name="code"
                            value={values.code}
                            error={!!errors.code}
                            helperText={touched.code && errors.code}
                          />
                        </div>

                        <div className="md:col-span-3 col-span-6">
                          <TextField
                            select
                            label="คำนำหน้า"
                            InputProps={{ sx: { borderRadius: "7px" } }}
                            fullWidth
                            disabled={isFormDisabled}
                            sx={{
                              ".Mui-disabled": {
                                background: "#F8F8F9",
                              },
                            }}
                            onChange={handleChange}
                            name="prefix"
                            value={values.prefix}
                            error={touched.prefix && !!errors.prefix}
                            helperText={touched.prefix && errors.prefix}
                          >
                            {prefixNames.map((prefix) => (
                              <MenuItem key={prefix.id} value={prefix.name}>
                                {prefix.name}
                              </MenuItem>
                            ))}
                          </TextField>
                        </div>

                        <div className="md:col-span-3 col-span-6">
                          <TextField
                            label="ชื่อ (TH)"
                            variant="outlined"
                            InputProps={{ sx: { borderRadius: "7px" } }}
                            fullWidth
                            disabled={isFormDisabled}
                            sx={{
                              ".Mui-disabled": {
                                background: "#F8F8F9",
                              },
                            }}
                            onChange={handleChange}
                            name="name"
                            value={values.name}
                            error={touched.name && !!errors.name}
                            helperText={touched.name && errors.name}
                          />
                        </div>

                        <div className="md:col-span-3 col-span-6">
                          <TextField
                            label="นามสกุล (TH)"
                            variant="outlined"
                            InputProps={{ sx: { borderRadius: "7px" } }}
                            fullWidth
                            disabled={isFormDisabled}
                            sx={{
                              ".Mui-disabled": {
                                background: "#F8F8F9",
                              },
                            }}
                            onChange={handleChange}
                            name="surname"
                            value={values.surname}
                            error={!!errors.surname}
                            helperText={touched.surname && errors.surname}
                          />
                        </div>

                        <div className="md:col-span-3 col-span-6">
                          <TextField
                            label="ชื่อเล่น"
                            variant="outlined"
                            InputProps={{ sx: { borderRadius: "7px" } }}
                            fullWidth
                            disabled={isFormDisabled}
                            sx={{
                              ".Mui-disabled": {
                                background: "#F8F8F9",
                              },
                            }}
                            onChange={handleChange}
                            name="nickname"
                            value={values.nickname}
                            error={touched.nickname && !!errors.nickname}
                            helperText={touched.nickname && errors.nickname}
                          />
                        </div>

                        <div className="md:col-span-3 col-span-6">
                          <TextField
                            select
                            label="เพศ"
                            InputProps={{ sx: { borderRadius: "7px" } }}
                            fullWidth
                            disabled={isFormDisabled}
                            sx={{
                              ".Mui-disabled": {
                                background: "#F8F8F9",
                              },
                            }}
                            onChange={handleChange}
                            name="gender"
                            value={values.gender}
                            error={touched.gender && !!errors.gender}
                            helperText={touched.gender && errors.gender}
                          >
                            {genders.map((gender) => (
                              <MenuItem
                                key={gender.id}
                                value={gender.id.toString()}
                              >
                                {gender.name}
                              </MenuItem>
                            ))}
                          </TextField>
                        </div>

                        <div className="md:col-span-3 col-span-6">
                          <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                              label="วัน/เดือน/ปี เกิด"
                              value={values.birth_date}
                              onChange={(date) => {
                                setFieldValue("birth_date", date);
                                setFieldValue("age", calculateAge(date));
                              }}
                              slotProps={{
                                textField: {
                                  InputProps: { sx: { borderRadius: "7px" } },
                                  fullWidth: true,
                                  error: !!errors.birth_date,
                                  helperText:
                                    touched.birth_date && errors.birth_date,
                                },
                              }}
                              sx={{
                                width: "100%",
                                ".Mui-disabled": {
                                  background: "#F8F8F9",
                                },
                              }}
                              disabled={isFormDisabled}
                            />
                          </LocalizationProvider>
                        </div>

                        <div className="md:col-span-3 col-span-6">
                          <TextField
                            label="อายุ"
                            variant="outlined"
                            InputProps={{
                              sx: { borderRadius: "7px" },
                              readOnly: true,
                            }}
                            fullWidth
                            disabled={true}
                            sx={{
                              ".Mui-disabled": {
                                background: "#F8F8F9",
                              },
                            }}
                            name="age"
                            value={values.age}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>

                <Card className="!rounded-lg mb-4">
                  <div className="p-4">
                    <div className="flex items-center mb-4 pb-2">
                      <div className="text-[#4C4E64] font-semibold text-xl mr-7">
                        ข้อมูลบัญชี
                      </div>
                      {adminID && (
                        <>
                          <div className="mr-4">
                            <Button
                              type="button"
                              className="btn-st-3 !bg-[#FF6B00] !rounded-lg !text-white px-4"
                              variant="contained"
                              onClick={() =>
                                setEmailModal((prev) => ({
                                  ...prev,
                                  open: true,
                                }))
                              }
                            >
                              เปลี่ยนอีเมล
                            </Button>
                          </div>
                          <div className="mr-4">
                            <Button
                              type="button"
                              className="btn-st-3 !bg-[#409FFB] !rounded-lg !text-white px-4"
                              variant="contained"
                              onClick={() =>
                                setResetPasswordModal((prev) => ({
                                  ...prev,
                                  open: true,
                                }))
                              }
                            >
                              รีเซ็ตรหัสผ่าน
                            </Button>
                          </div>
                        </>
                      )}
                    </div>

                    <div className="grid grid-cols-12 gap-4">
                      <div className="md:col-span-4 col-span-12">
                        <TextField
                          label="อีเมล"
                          variant="outlined"
                          InputProps={{ sx: { borderRadius: "7px" } }}
                          fullWidth
                          disabled={!!adminID}
                          sx={{
                            ".Mui-disabled": {
                              background: "#F8F8F9",
                            },
                          }}
                          onChange={handleChange}
                          name="empEmail"
                          value={values.empEmail}
                          error={!!errors.empEmail}
                          helperText={touched.empEmail && errors.empEmail}
                        />
                      </div>
                      <div className="md:col-span-4 col-span-12">
                        <TextField
                          label="รหัสผ่าน"
                          type="password"
                          variant="outlined"
                          InputProps={{ sx: { borderRadius: "7px" } }}
                          fullWidth
                          disabled={!!adminID}
                          sx={{
                            ".Mui-disabled": {
                              background: "#F8F8F9",
                            },
                          }}
                          onChange={handleChange}
                          name="password"
                          value={values.password}
                          error={!!errors.password}
                          helperText={touched.password && errors.password}
                        />
                      </div>
                    </div>
                  </div>
                </Card>

                <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4">
                  <div className="max-w-7xl mx-auto">
                    <div className="flex justify-end gap-4">
                      {!isFormDisabled ? (
                        <>
                          <Button
                            variant="outlined"
                            className="!border-[#6D788D] !rounded-lg !text-[#6D788D]"
                            onClick={() => setIsFormDisabled(true)}
                          >
                            ยกเลิก
                          </Button>
                          <Button
                            variant="contained"
                            className="!bg-[#409FFB] !rounded-lg !text-white"
                            type="submit"
                          >
                            บันทึก
                          </Button>
                        </>
                      ) : (
                        <Button
                          variant="contained"
                          className="!bg-[#409FFB] !rounded-lg !text-white"
                          type="button"
                          onClick={() => setIsFormDisabled(false)}
                        >
                          แก้ไข
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        </div>

        <ChangeEmailModal
          modal={emailModal}
          setModal={setEmailModal}
          setModalEmailSuccess={setEmailSuccessModal}
          formaccount={formData}
          adminID={adminID}
          refreshData={fetchUserData}
        />

        <ResetpasswordModal
          modal={resetPasswordModal}
          setModal={setResetPasswordModal}
          setModalResetPasswordSuccess={setResetPasswordSuccessModal}
          adminID={adminID}
          refreshData={fetchUserData}
        />

        <ResetpasswordSuccessModal
          modal={resetPasswordSuccessModal}
          setModal={setResetPasswordSuccessModal}
        />

        <ChangeEmailSuccessModal
          modal={emailSuccessModal}
          setModal={setEmailSuccessModal}
        />
      </div>
      <UserAdminSuccessModal modal={modalSuccess} setModal={setModalSuccess} />
    </>
  );
}
